/**
 * Quick emoji reactions + public like control for live chat bubbles.
 */

import React, { useState } from "react";
import { Heart } from "lucide-react";
import { CHAT_QUICK_EMOJIS, type ChatMessage } from "./liveCollaborationTypes";

type Props = {
  message: ChatMessage;
  /** Like only on Everyone / public channel. */
  allowLike: boolean;
  busy?: boolean;
  onLike: () => void;
  onReact: (emoji: string) => void;
};

function ChatMessageActionsInner({
  message,
  allowLike,
  busy = false,
  onLike,
  onReact,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactions = message.reactions || [];
  const likeCount = message.like_count || 0;
  const liked = Boolean(message.i_liked);

  return (
    <div style={{ marginTop: 6 }}>
      {(reactions.length > 0 || (allowLike && likeCount > 0)) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            marginBottom: 4,
          }}
        >
          {allowLike && likeCount > 0 ? (
            <button
              type="button"
              disabled={busy}
              onClick={onLike}
              style={{
                border: liked
                  ? "1px solid rgba(248,113,113,0.55)"
                  : "1px solid rgba(255,255,255,0.12)",
                background: liked
                  ? "rgba(248,113,113,0.18)"
                  : "rgba(0,0,0,0.25)",
                color: liked ? "#fecaca" : "#94a3b8",
                borderRadius: 999,
                padding: "2px 8px",
                fontSize: 11,
                fontWeight: 700,
                cursor: busy ? "wait" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Heart
                size={11}
                fill={liked ? "currentColor" : "none"}
              />
              {likeCount}
            </button>
          ) : null}
          {reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              disabled={busy}
              onClick={() => onReact(r.emoji)}
              title={r.emoji}
              style={{
                border: r.i_reacted
                  ? "1px solid rgba(96,165,250,0.55)"
                  : "1px solid rgba(255,255,255,0.12)",
                background: r.i_reacted
                  ? "rgba(37,99,235,0.25)"
                  : "rgba(0,0,0,0.25)",
                color: "#e2e8f0",
                borderRadius: 999,
                padding: "2px 7px",
                fontSize: 12,
                cursor: busy ? "wait" : "pointer",
              }}
            >
              {r.emoji} {r.count}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 4,
        }}
      >
        {allowLike ? (
          <button
            type="button"
            disabled={busy}
            onClick={onLike}
            aria-label={liked ? "Unlike" : "Like"}
            title={liked ? "Unlike" : "Like"}
            style={{
              border: "none",
              background: "transparent",
              color: liked ? "#f87171" : "#64748b",
              padding: "2px 4px",
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: 1,
            }}
          >
            <Heart size={13} fill={liked ? "currentColor" : "none"} />
          </button>
        ) : null}
        <button
          type="button"
          disabled={busy}
          onClick={() => setPickerOpen((v) => !v)}
          aria-label="Add reaction"
          title="React"
          style={{
            border: "none",
            background: "transparent",
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
            padding: "2px 4px",
            cursor: busy ? "wait" : "pointer",
          }}
        >
          ☺
        </button>
        {pickerOpen ? (
          <div
            style={{
              display: "inline-flex",
              flexWrap: "wrap",
              gap: 2,
              padding: "3px 4px",
              borderRadius: 999,
              background: "rgba(15,23,42,0.92)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            {CHAT_QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                disabled={busy}
                onClick={() => {
                  onReact(emoji);
                  setPickerOpen(false);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 14,
                  lineHeight: 1,
                  padding: "3px 4px",
                  cursor: busy ? "wait" : "pointer",
                  borderRadius: 6,
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const ChatMessageActions = React.memo(ChatMessageActionsInner);
export { ChatMessageActions };
export default ChatMessageActions;
